import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { CreateEnvironmentData, Environment } from '@/types/environment';
import { EnvironmentType, EnvironmentStatus } from '@/types/api';
import { ArrowLeft, Layers } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea, ErrorAlert } from '@nimbitlabs/ui';

const environmentTypes: { value: EnvironmentType; label: string }[] = [
  { value: EnvironmentType.Development, label: 'Development' },
  { value: EnvironmentType.Uat, label: 'UAT' },
  { value: EnvironmentType.Staging, label: 'Staging' },
  { value: EnvironmentType.Production, label: 'Production' },
];

const statusOptions: { value: EnvironmentStatus; label: string }[] = [
  { value: EnvironmentStatus.Active, label: 'Active' },
  { value: EnvironmentStatus.Inactive, label: 'Inactive' },
  { value: EnvironmentStatus.Maintenance, label: 'Maintenance' },
];

export const CreateEnvironment: React.FC = () => {
  const navigate = useNavigate();
  const { addEnvironment } = useEnvironmentsStore();
  
  const [formData, setFormData] = useState<CreateEnvironmentData>({
    name: '',
    description: '',
    type: EnvironmentType.Development,
    status: EnvironmentStatus.Active,
    url: '',
  });

  const [error, setError] = useState<unknown>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    try {
      await addEnvironment(formData);
      navigate('/environments');
    } catch (err) {
      console.error('Failed to create environment:', err);
      setError(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/environments">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Environments
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create New Environment</CardTitle>
          <CardDescription>
            Create a new environment for your application deployment.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ErrorAlert error={error} className="mb-4" />
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Environment name"
                required
                maxLength={200}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(value: Environment['type']) => setFormData(prev => ({ ...prev, type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select environment type" />
                </SelectTrigger>
                <SelectContent>
                  {environmentTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Environment description"
                rows={4}
                required
                maxLength={1000}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(value: Environment['status']) => setFormData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate('/environments')}>
                Cancel
              </Button>
              <Button type="submit">
                Create Environment
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
